import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  FormField,
  StatusBadge,
  SubTable,
  Textarea,
  Timestamp,
  type SubTableColumn,
} from "@/design-system/components/ui";
import { PageHeader } from "@/components/layout/PageHeader";
import { ConfirmModal } from "@/components/ui/PromptModals";
import { useToast } from "@/components/ui/Toast";
import { extractErrorMessage } from "@/api/client";
import {
  hasHardError,
  validateEmailTemplateBlock,
} from "@/lib/emailTemplateValidation";
import {
  useEmailTemplate,
  usePreviewEmailTemplate,
  usePublishEmailTemplate,
  useResetEmailTemplate,
  useRestoreEmailTemplate,
  useSaveEmailTemplateDraft,
} from "@/hooks/emailTemplates/useEmailTemplates";
import type {
  EmailTemplateBlock,
  EmailTemplateEditor,
  EmailTemplateFinding,
  EmailTemplatePreview,
  EmailTemplatePreviewBlockInput,
  EmailTemplateVersion,
} from "@/types/emailTemplates";

/** A field the author can type in: one block's HTML wording, or its plain-text
 *  variant. The two share a base block (and therefore a kind and a token
 *  whitelist); only the storage key differs. */
interface Field {
  /** The key this field's override row is stored under (`{block}` or `{block}:text`). */
  storageKey: string;
  /** The BASE block key — what the write endpoint takes. */
  blockKey: string;
  textVariant: boolean;
  label: string;
  defaultText: string;
  liveText: string;
  block: EmailTemplateBlock;
}

function fieldsFor(block: EmailTemplateBlock): Field[] {
  const html: Field = {
    storageKey: block.blockKey,
    blockKey: block.blockKey,
    textVariant: false,
    label: block.kind === "Subject" ? "Subject line" : "HTML wording",
    defaultText: block.defaultText,
    liveText: block.effectiveText,
    block,
  };
  if (!block.textVariant) return [html];
  return [
    html,
    {
      storageKey: block.textVariant.storageBlockKey,
      blockKey: block.blockKey,
      textVariant: true,
      label: "Plain-text wording",
      defaultText: block.textVariant.defaultText,
      liveText: block.textVariant.effectiveText,
      block,
    },
  ];
}

function allFields(editor: EmailTemplateEditor): Field[] {
  return editor.blocks.flatMap(fieldsFor);
}

/** What the author was last working on: their unpublished draft if there is one,
 *  otherwise what is live today. */
function seedTexts(editor: EmailTemplateEditor): Record<string, string> {
  const seeded: Record<string, string> = {};
  for (const block of editor.blocks) {
    seeded[block.blockKey] = block.draftText ?? block.effectiveText;
    if (block.textVariant) {
      seeded[block.textVariant.storageBlockKey] =
        block.textVariant.draftText ?? block.textVariant.effectiveText;
    }
  }
  return seeded;
}

/**
 * The per-block editor for one outbound email.
 *
 * 🔴 **Saving is not sending.** Every field shows three things that are routinely
 * different — the code default, what is LIVE today, and the author's working text —
 * because the one mistake this surface can make is letting someone believe an
 * unpublished edit is going out. Publish is the only action that changes what sends,
 * and it is the only one styled as primary.
 *
 * 🔴 **A hard validation error blocks submit**, so the text never leaves the
 * textarea. The server refuses the same edit for defence in depth; blocking here is
 * what keeps the two ends telling the same story about whether the work was kept.
 */
export function EmailTemplateEditorPage() {
  const params = useParams<{ key: string }>();
  const templateKey = params.key ?? "";
  const toast = useToast();

  const query = useEmailTemplate(templateKey);
  const editor = query.data;

  const saveDraft = useSaveEmailTemplateDraft(templateKey);
  const publish = usePublishEmailTemplate(templateKey);
  const restore = useRestoreEmailTemplate(templateKey);
  const reset = useResetEmailTemplate(templateKey);
  const preview = usePreviewEmailTemplate(templateKey);

  const [texts, setTexts] = useState<Record<string, string>>({});
  // Bumped by publish / restore / reset so the working text re-seeds from the
  // fresh server state. A plain "re-seed whenever data changes" would clobber
  // in-progress typing in every OTHER field each time one field is saved.
  const [seed, setSeed] = useState(0);
  const [seededFor, setSeededFor] = useState<string | null>(null);

  const [confirmReset, setConfirmReset] = useState(false);
  const [confirmRestore, setConfirmRestore] = useState<number | null>(null);
  const [rendered, setRendered] = useState<EmailTemplatePreview | null>(null);

  useEffect(() => {
    if (!editor) return;
    const token = `${editor.key}#${seed}`;
    if (seededFor === token) return;
    setTexts(seedTexts(editor));
    setSeededFor(token);
  }, [editor, seed, seededFor]);

  const fields = useMemo(() => (editor ? allFields(editor) : []), [editor]);

  const findingsByField = useMemo(() => {
    const map: Record<string, EmailTemplateFinding[]> = {};
    for (const field of fields) {
      const text = texts[field.storageKey] ?? "";
      map[field.storageKey] = validateEmailTemplateBlock(field.block, text, field.storageKey);
    }
    return map;
  }, [fields, texts]);

  const anyHardError = useMemo(
    () => Object.values(findingsByField).some(hasHardError),
    [findingsByField],
  );

  const pendingDrafts = useMemo(
    () =>
      (editor?.blocks ?? []).reduce(
        (n, b) => n + (b.hasDraft ? 1 : 0) + (b.textVariant?.hasDraft ? 1 : 0),
        0,
      ),
    [editor],
  );

  const reseed = () => setSeed((s) => s + 1);

  const handleSave = (field: Field) => {
    void saveDraft
      .mutateAsync({
        blockKey: field.blockKey,
        text: texts[field.storageKey] ?? "",
        textVariant: field.textVariant,
      })
      .then((result) => {
        if (result.warnings.length > 0) {
          toast.info(`Draft saved with ${result.warnings.length} note(s). It won't send until you publish.`);
        } else {
          toast.success("Draft saved. It won't send until you publish.");
        }
      })
      .catch((e) => toast.error(extractErrorMessage(e)));
  };

  const handlePublish = () => {
    void publish
      .mutateAsync({ note: null })
      .then(() => {
        reseed();
        toast.success("Published. This is what sends from now on.");
      })
      .catch((e) => toast.error(extractErrorMessage(e)));
  };

  const handleRestore = (versionNumber: number) => {
    setConfirmRestore(null);
    void restore
      .mutateAsync(versionNumber)
      .then(() => {
        reseed();
        toast.success(`Restored version ${versionNumber}.`);
      })
      .catch((e) => toast.error(extractErrorMessage(e)));
  };

  const handleReset = () => {
    setConfirmReset(false);
    void reset
      .mutateAsync()
      .then(() => {
        reseed();
        toast.success("Reset. The code defaults are sending again.");
      })
      .catch((e) => toast.error(extractErrorMessage(e)));
  };

  const handlePreview = () => {
    const blocks: EmailTemplatePreviewBlockInput[] = fields.map((f) => ({
      blockKey: f.blockKey,
      text: texts[f.storageKey] ?? "",
      textVariant: f.textVariant,
    }));
    void preview
      .mutateAsync(blocks)
      .then(setRendered)
      .catch((e) => toast.error(extractErrorMessage(e)));
  };

  if (query.isLoading) {
    return <p className="text-small text-gray-mid">Loading…</p>;
  }
  if (query.isError || !editor) {
    return (
      <div className="flex flex-col gap-space-3">
        <PageHeader
          eyebrow="Admin"
          title="Email template"
          breadcrumbs={[
            { label: "Email Templates", to: "/admin/email-templates" },
            { label: templateKey },
          ]}
        />
        <p className="text-small text-status-error">
          {query.isError ? extractErrorMessage(query.error) : "Not found."}
        </p>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-space-4">
      <PageHeader
        eyebrow="Admin"
        title={editor.key}
        description={editor.description}
        breadcrumbs={[
          { label: "Email Templates", to: "/admin/email-templates" },
          { label: editor.key },
        ]}
        actions={
          <div className="flex flex-wrap items-center gap-space-2">
            <Button variant="secondary" onClick={handlePreview} loading={preview.isPending}>
              Preview
            </Button>
            <Button
              variant="text"
              onClick={() => setConfirmReset(true)}
              disabled={reset.isPending}
            >
              Reset to defaults
            </Button>
            <Button
              onClick={handlePublish}
              loading={publish.isPending}
              disabled={anyHardError || pendingDrafts === 0}
            >
              Publish
            </Button>
          </div>
        }
      />

      <Card>
        <CardContent className="flex flex-col gap-space-2">
          <div className="flex flex-wrap items-center gap-space-2">
            <StatusBadge tone={editor.audience === "Internal" ? "neutral" : "info"}>
              {editor.audienceLabel}
            </StatusBadge>
            {pendingDrafts > 0 ? (
              <StatusBadge tone="warn">{pendingDrafts} unpublished edit(s)</StatusBadge>
            ) : (
              <StatusBadge tone="ok">Nothing pending</StatusBadge>
            )}
          </div>
          <p className="text-caption text-gray-mid">Sent when: {editor.trigger}</p>
          <p className="text-caption text-gray-mid">
            Saving an edit stores a draft. The previous wording keeps sending until you publish.
          </p>
        </CardContent>
      </Card>

      {editor.blocks.map((block) => (
        <BlockCard
          key={block.blockKey}
          block={block}
          texts={texts}
          findingsByField={findingsByField}
          saving={saveDraft.isPending}
          onChange={(storageKey, value) =>
            setTexts((prev) => ({ ...prev, [storageKey]: value }))
          }
          onSave={handleSave}
        />
      ))}

      {rendered && <PreviewPanel preview={rendered} onClose={() => setRendered(null)} />}

      <VersionHistory
        versions={editor.versions}
        busy={restore.isPending}
        onRestore={(v) => setConfirmRestore(v)}
      />

      <ConfirmModal
        open={confirmReset}
        title="Reset to the code defaults?"
        message={
          <span>
            Every override on <strong>{editor.key}</strong> is cleared, drafts included, and the
            built-in wording sends again immediately. The change is recorded as a version, so the
            current copy can be restored from the history below.
          </span>
        }
        confirmLabel="Reset"
        destructive
        onConfirm={handleReset}
        onCancel={() => setConfirmReset(false)}
      />

      <ConfirmModal
        open={confirmRestore != null}
        title={`Restore version ${confirmRestore ?? ""}?`}
        message="The wording from that version becomes live immediately, and the rollback is appended to the history as a new version — nothing is deleted, so it can be undone the same way."
        confirmLabel="Restore"
        onConfirm={() => confirmRestore != null && handleRestore(confirmRestore)}
        onCancel={() => setConfirmRestore(null)}
      />
    </div>
  );
}

// ── one block ───────────────────────────────────────────────────────────────

function BlockCard({
  block,
  texts,
  findingsByField,
  saving,
  onChange,
  onSave,
}: {
  block: EmailTemplateBlock;
  texts: Record<string, string>;
  findingsByField: Record<string, EmailTemplateFinding[]>;
  saving: boolean;
  onChange: (storageKey: string, value: string) => void;
  onSave: (field: Field) => void;
}) {
  const fields = fieldsFor(block);

  return (
    <Card>
      <CardHeader className="flex flex-wrap items-center justify-between gap-space-2">
        <CardTitle className="font-mono">{block.blockKey}</CardTitle>
        <div className="flex flex-wrap items-center gap-space-2">
          <StatusBadge tone="neutral">{block.kind}</StatusBadge>
          {block.isCustomized && <StatusBadge tone="info">Live override</StatusBadge>}
          {block.hasDraft && <StatusBadge tone="warn">Draft pending</StatusBadge>}
        </div>
      </CardHeader>
      <CardContent className="flex flex-col gap-space-4">
        {block.allowedTokens.length > 0 && (
          <p className="text-caption text-gray-mid">
            Placeholders:{" "}
            {block.allowedTokens.map((t) => (
              <code key={t} className="mr-space-1 font-mono">{`{{${t}}}`}</code>
            ))}
            {block.requiredTokens.length > 0 && (
              <> — required: {block.requiredTokens.map((t) => `{{${t}}}`).join(", ")}</>
            )}
          </p>
        )}

        {!block.textVariant && (
          <p className="text-caption text-gray-mid">
            The plain-text part of this email says exactly what the HTML part says, so there is one
            wording to edit.
          </p>
        )}

        {fields.map((field) => (
          <FieldEditor
            key={field.storageKey}
            field={field}
            value={texts[field.storageKey] ?? ""}
            findings={findingsByField[field.storageKey] ?? []}
            saving={saving}
            onChange={onChange}
            onSave={onSave}
          />
        ))}
      </CardContent>
    </Card>
  );
}

function FieldEditor({
  field,
  value,
  findings,
  saving,
  onChange,
  onSave,
}: {
  field: Field;
  value: string;
  findings: EmailTemplateFinding[];
  saving: boolean;
  onChange: (storageKey: string, value: string) => void;
  onSave: (field: Field) => void;
}) {
  const blocked = hasHardError(findings);
  const isDefault = value === field.defaultText;
  const differsFromLive = value !== field.liveText;

  return (
    <div className="flex flex-col gap-space-2">
      <FormField
        label={field.label}
        error={blocked ? findings.find((f) => f.severity === "Error")?.message : undefined}
      >
        <Textarea
          value={value}
          rows={field.block.kind === "Subject" ? 2 : 5}
          invalid={blocked}
          onChange={(e) => onChange(field.storageKey, e.target.value)}
        />
      </FormField>

      {findings.filter((f) => f.severity === "Warning").map((f, i) => (
        <p key={i} className="text-caption text-status-warning-ink">
          {f.message}
        </p>
      ))}

      <details className="text-caption text-gray-mid">
        <summary className="cursor-pointer">Code default and what is live now</summary>
        <div className="mt-space-2 flex flex-col gap-space-2">
          <div>
            <span className="uppercase tracking-wide">Code default</span>
            <pre className="mt-space-1 whitespace-pre-wrap break-words font-mono text-caption">
              {field.defaultText === "" ? "(empty in this part)" : field.defaultText}
            </pre>
          </div>
          <div>
            <span className="uppercase tracking-wide">Sending today</span>
            <pre className="mt-space-1 whitespace-pre-wrap break-words font-mono text-caption">
              {field.liveText === "" ? "(empty in this part)" : field.liveText}
            </pre>
          </div>
        </div>
      </details>

      <div className="flex flex-wrap items-center gap-space-2">
        <Button
          size="sm"
          variant="secondary"
          onClick={() => onSave(field)}
          disabled={blocked || saving || !differsFromLive}
          loading={saving}
        >
          Save draft
        </Button>
        <Button
          size="sm"
          variant="text"
          onClick={() => onChange(field.storageKey, field.defaultText)}
          disabled={isDefault}
        >
          Use code default
        </Button>
        {blocked && (
          <span className="text-caption text-status-error">
            Fix the error above before saving — nothing is stored until you do.
          </span>
        )}
      </div>
    </div>
  );
}

// ── preview ─────────────────────────────────────────────────────────────────

function PreviewPanel({
  preview,
  onClose,
}: {
  preview: EmailTemplatePreview;
  onClose: () => void;
}) {
  return (
    <Card>
      <CardHeader className="flex flex-wrap items-center justify-between gap-space-2">
        <CardTitle>Preview</CardTitle>
        <Button size="sm" variant="text" onClick={onClose}>
          Close
        </Button>
      </CardHeader>
      <CardContent className="flex flex-col gap-space-3">
        <p className="text-caption text-gray-mid">
          Rendered through the real branded email renderer with sample values, then through the same
          step that appends the regulated-role disclaimer at send — so counterparty mail previews
          with it and internal mail without it, exactly as each will arrive. A placeholder with no
          sample shows as its own name in square brackets, so a stand-in never reads as your copy.
        </p>

        <div>
          <span className="text-label uppercase tracking-wide text-gray-mid">Subject</span>
          <p className="text-small">{preview.subject}</p>
        </div>

        <div>
          <span className="text-label uppercase tracking-wide text-gray-mid">HTML part</span>
          <iframe
            title="Email preview"
            // Sandboxed with no allow-* tokens: the preview renders admin-authored
            // copy through the branded renderer, and there is nothing in it that
            // needs to run.
            sandbox=""
            srcDoc={preview.html}
            className="mt-space-1 h-[28rem] w-full rounded-md border border-border-light bg-white-pure"
          />
        </div>

        <div>
          <span className="text-label uppercase tracking-wide text-gray-mid">Plain-text part</span>
          <pre className="mt-space-1 overflow-x-auto whitespace-pre-wrap break-words rounded-md border border-border-light bg-white-warm p-space-3 font-mono text-caption">
            {preview.text}
          </pre>
        </div>
      </CardContent>
    </Card>
  );
}

// ── history ─────────────────────────────────────────────────────────────────

function VersionHistory({
  versions,
  busy,
  onRestore,
}: {
  versions: EmailTemplateVersion[];
  busy: boolean;
  onRestore: (versionNumber: number) => void;
}) {
  const columns: SubTableColumn<EmailTemplateVersion>[] = [
    { key: "v", header: "Version", render: (v) => `v${v.versionNumber}` },
    {
      key: "when",
      header: "Published",
      render: (v) => <Timestamp value={v.publishedAtUtc} />,
    },
    { key: "who", header: "By", render: (v) => v.publishedByUserId ?? "—" },
    {
      key: "blocks",
      header: "Blocks live",
      align: "right",
      render: (v) => v.blockCount,
    },
    { key: "note", header: "Note", render: (v) => v.note ?? "—" },
    {
      key: "restore",
      header: "",
      render: (v) => (
        <Button size="sm" variant="text" disabled={busy} onClick={() => onRestore(v.versionNumber)}>
          Restore
        </Button>
      ),
    },
  ];

  return (
    <Card>
      <CardHeader>
        <CardTitle>Publish history</CardTitle>
      </CardHeader>
      <CardContent>
        <p className="mb-space-2 text-caption text-gray-mid">
          The newest version is what sends. A restore is appended as a new version rather than
          deleting anything, so a rollback can itself be rolled back.
        </p>
        <SubTable
          columns={columns}
          rows={versions}
          rowKey={(v) => String(v.versionNumber)}
          caption="Publish history"
          empty={{ message: "Nothing has been published — the code defaults are sending." }}
        />
      </CardContent>
    </Card>
  );
}
